export interface PropertyInfo {
  label: string;
  name: string;
  kind: number;
}

export type PropertyModel = Record<string, PropertyInfo[]>;

interface TreeNode {
  children: Record<string, TreeNode>;
  fullPath: string;
  name: string;
  isLeaf: boolean;
  isRef: boolean;
  depth: number;
}

export class PropertyChainParser {
  private _model: PropertyModel = {};

  constructor(private propertyChains: string[]) {}

  parse(): PropertyModel {
    // build tree structure
    const root: TreeNode = {
      children: {},
      fullPath: '',
      name: '',
      isLeaf: false,
      isRef: false,
      depth: 0,
    };

    for (const chain of this.propertyChains) {
      const words = chain.split('.');
      let node = root;
      let path = '';
      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        path = path !== '' ? path + '.' + word : word;
        if (node.children[word] == null) {
          node.children[word] = {
            children: {},
            fullPath: path,
            name: word,
            isLeaf: false,
            isRef: word === '@' ? true : false,
            depth: i + 1,
          };
        }
        node = node.children[word];
        if (i === words.length - 1) {
          node.isLeaf = true;
        }
      }
    }

    // start recursion from each top level node
    for (const key of Object.keys(root.children)) {
      this.buildPropertyModel(root.children[key]);
    }

    return this._model;
  }

  private buildPropertyModel(node: TreeNode) {
    const keys = Object.keys(node.children);
    // only add nodes that have children
    if (node.fullPath != null && keys.length > 0) {
      const properties: PropertyInfo[] = [];
      // add itself if present and is root (depth === 1)
      if (node.isLeaf && node.depth === 1) {
        properties.push({
          label: node.fullPath,
          name: node.name,
          kind: node.isRef
            ? monaco.languages.CompletionItemKind.Reference
            : monaco.languages.CompletionItemKind.Variable,
        });
      }
      // add children
      for (const key of keys) {
        const child = node.children[key];
        properties.push({
          label: child.fullPath,
          name: child.name,
          kind: child.isRef
            ? monaco.languages.CompletionItemKind.Reference
            : monaco.languages.CompletionItemKind.Property,
        });
      }
      this._model[node.fullPath] = properties;
    }
    // recurse into children
    for (const key of keys) {
      this.buildPropertyModel(node.children[key]);
    }
  }
}
